import React, { useState } from 'react';
import { ApplicationData } from '../LoanApplicationFlow';
import '../StepStyles.css';

interface Step3BankAccountProps {
  data: ApplicationData;
  onUpdate: (data: Partial<ApplicationData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// South African banks with their branch codes
const SA_BANKS = [
  { name: 'ABSA Bank', code: 'absa', branchCode: '632005' },
  { name: 'Standard Bank', code: 'standard', branchCode: '051001' },
  { name: 'First National Bank (FNB)', code: 'fnb', branchCode: '250655' },
  { name: 'Nedbank', code: 'nedbank', branchCode: '198765' },
  { name: 'Capitec Bank', code: 'capitec', branchCode: '470010' },
  { name: 'Discovery Bank', code: 'discovery', branchCode: '679000' },
  { name: 'TymeBank', code: 'tyme', branchCode: '678910' },
  { name: 'African Bank', code: 'african', branchCode: '430000' },
  { name: 'Bidvest Bank', code: 'bidvest', branchCode: '462005' },
  { name: 'Investec Bank', code: 'investec', branchCode: '580105' },
  { name: 'Other', code: 'other', branchCode: '' }
];

const Step3BankAccount: React.FC<Step3BankAccountProps> = ({
  data,
  onUpdate,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState({
    bankName: data.bankName,
    accountNumber: data.accountNumber,
    accountType: data.accountType,
    branchCode: data.branchCode
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Auto-fill branch code when bank is selected
    if (field === 'bankName') {
      const selectedBank = SA_BANKS.find(bank => bank.name === value);
      if (selectedBank && selectedBank.branchCode) {
        const newBranchCode = selectedBank.branchCode;
        setFormData(prev => ({ ...prev, branchCode: newBranchCode }));
        onUpdate({ [field]: value, branchCode: newBranchCode });
        return;
      }
    }
    
    // Update parent data
    onUpdate({ [field]: value });
  };

  // Validate account number
  const validateAccountNumber = (accountNumber: string): boolean => {
    // Remove spaces and check if it's numeric
    const cleanNumber = accountNumber.replace(/\s/g, '');
    
    // SA account numbers are typically 9-11 digits
    if (!/^\d{9,11}$/.test(cleanNumber)) return false;
    
    return true;
  };

  // Validate branch code
  const validateBranchCode = (branchCode: string): boolean => {
    // SA branch codes are typically 6 digits
    return /^\d{6}$/.test(branchCode);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Bank name validation
    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Please select your bank';
    }
    
    // Account number validation
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!validateAccountNumber(formData.accountNumber)) {
      newErrors.accountNumber = 'Please enter a valid account number (9-11 digits)';
    }
    
    // Account type validation
    if (!formData.accountType) {
      newErrors.accountType = 'Please select your account type';
    }
    
    // Branch code validation
    if (!formData.branchCode.trim()) {
      newErrors.branchCode = 'Branch code is required';
    } else if (!validateBranchCode(formData.branchCode)) {
      newErrors.branchCode = 'Please enter a valid 6-digit branch code';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle continue
  const handleContinue = async () => {
    setIsValidating(true);
    
    if (validateForm()) {
      // Update parent with current data
      onUpdate(formData);
      
      // Simulate API validation
      await new Promise(resolve => setTimeout(resolve, 800));
      
      onNext();
    }
    
    setIsValidating(false);
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <h2 className="step-title">Add Your Bank Account</h2>
        <p className="step-description">
          Please provide the bank account where your <strong>NSFAS payments are deposited</strong>. 
          We'll use this account to transfer your loan and collect repayment. 
          Your information is secure and encrypted.
        </p>
      </div>

      <div className="step-content">
        <form className="bank-account-form" onSubmit={(e) => e.preventDefault()}>
          {/* Bank Selection */}
          <div className="form-group">
            <label htmlFor="bankName" className="form-label">
              Bank Name *
            </label>
            <select
              id="bankName"
              className={`form-select ${errors.bankName ? 'error' : ''}`}
              value={formData.bankName}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
            >
              <option value="">Select your bank</option>
              {SA_BANKS.map((bank) => (
                <option key={bank.code} value={bank.name}>
                  {bank.name}
                </option>
              ))}
            </select>
            {errors.bankName && (
              <span className="error-message">{errors.bankName}</span>
            )}
          </div>

          {/* Account Details Row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="accountNumber" className="form-label">
                Account Number *
              </label>
              <input
                type="text"
                id="accountNumber"
                className={`form-input ${errors.accountNumber ? 'error' : ''}`}
                value={formData.accountNumber}
                onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/\D/g, ''))}
                placeholder="1234567890"
                maxLength={11}
              />
              {errors.accountNumber && (
                <span className="error-message">{errors.accountNumber}</span>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="accountType" className="form-label">
                Account Type *
              </label>
              <select
                id="accountType"
                className={`form-select ${errors.accountType ? 'error' : ''}`}
                value={formData.accountType}
                onChange={(e) => handleInputChange('accountType', e.target.value)}
              >
                <option value="">Select type</option>
                <option value="savings">Savings</option>
                <option value="cheque">Cheque</option>
              </select>
              {errors.accountType && (
                <span className="error-message">{errors.accountType}</span>
              )}
            </div>
          </div>

          {/* Branch Code */}
          <div className="form-group">
            <label htmlFor="branchCode" className="form-label">
              Branch Code *
            </label>
            <input
              type="text"
              id="branchCode"
              className={`form-input ${errors.branchCode ? 'error' : ''}`}
              value={formData.branchCode}
              onChange={(e) => handleInputChange('branchCode', e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              maxLength={6}
              readOnly={formData.bankName !== 'Other'}
            />
            {errors.branchCode && (
              <span className="error-message">{errors.branchCode}</span>
            )}
            {formData.bankName && formData.bankName !== 'Other' && (
              <span className="field-hint">
                ✓ Branch code auto-filled for {formData.bankName}
              </span>
            )}
          </div>
        </form>

        {/* Account Verification Info */}
        <div className="verification-section">
          <div className="verification-card">
            <h4 className="verification-title">🔍 Account Verification</h4>
            <p className="verification-description">
              We'll verify your account details to ensure secure transactions. This may include:
            </p>
            <ul className="verification-list">
              <li>Confirming account ownership matches your ID</li>
              <li>Checking account is active and in good standing</li>
              <li>Verifying NSFAS payments are received in this account</li>
              <li>Testing small deposit/withdrawal (reversed immediately)</li>
            </ul>
          </div>
        </div>

        {/* Security Information */}
        <div className="modern-info-section">
          <div className="info-header">
            <div className="info-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="16" r="1"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div className="info-header-text">
              <h4>Your Security is Our Priority</h4>
              <p>Bank-grade encryption and secure processing</p>
            </div>
          </div>
          
          <div className="info-items">
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <circle cx="12" cy="16" r="1"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Bank-Grade Encryption</span>
                <span className="info-item-description">All bank details are encrypted with bank-grade security</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Secure Storage</span>
                <span className="info-item-description">We never store your full account details on our servers</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Quick Processing</span>
                <span className="info-item-description">Full process from application to funds in your account within 24 hours</span>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-item-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l10 5-10 5L2 7l10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div className="info-item-content">
                <span className="info-item-title">Secure Banking</span>
                <span className="info-item-description">Repayments are processed through secure banking channels</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Actions */}
      <div className="step-actions">
        <button
          className="back-btn secondary"
          onClick={onBack}
          type="button"
        >
          Back
        </button>
        
        <button
          className="continue-btn primary"
          onClick={handleContinue}
          disabled={isValidating}
          type="button"
        >
          {isValidating ? (
            <>
              <span className="btn-spinner"></span>
              Verifying Account...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14"/>
                <path d="M12 5l7 7-7 7"/>
              </svg>
              Continue to NSFAS Verification
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Step3BankAccount;

